/**
 * App.tsx — Haupt-Router und Layout
 * Routing: /login, /akten/:betrieb, /akten/:betrieb/:id/:section, /crm/:tab, etc.
 * Kiosk-Routen: /kiosk/kunden, /kiosk/werkstatt (fullscreen, ohne Layout)
 * Auth: Supabase Auth mit Login-Seite, Protected Routes
 *
 * v6.0: CRM + Informationsschreiben Routen hinzugefügt
 */

import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Route, Switch, Redirect, useLocation } from 'wouter';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import AktenPage from './pages/AktenPage';
import AkteDetail from './pages/AkteDetail';
import AufgabenPage from './pages/AufgabenPage';
import KalenderPage from './pages/KalenderPage';
import KontaktePage from './pages/KontaktePage';
import KontaktDetailPage from './pages/KontaktDetailPage';
import BuchhaltungPage from './pages/BuchhaltungPage';
import FinanzenDashboard from './pages/FinanzenDashboard';
import EinstellungenPage from './pages/EinstellungenPage';
import DokumentePage from './pages/DokumentePage';
import LoginPage from './pages/LoginPage';
import KundenKiosk from './pages/KundenKiosk';
import KommunikationPage from './pages/KommunikationPage';
import EntwuerfeFreigabePage from './pages/EntwuerfeFreigabePage';
import StellungnahmenPage from './pages/StellungnahmenPage';
import RechtsAktenPage from './pages/RechtsAktenPage';
import ForderungsmanagementPage from './pages/ForderungsmanagementPage';
import ForderungsmanagerPage from './pages/ForderungsmanagerPage';
import ForderungsCockpitPage from './pages/ForderungsCockpitPage';
import MailboxReconnectPage from './pages/MailboxReconnectPage';
import FahrzeugePage from './pages/FahrzeugePage';
import FahrzeugDetail from './pages/FahrzeugDetail';
import FahrzeugQRScanPage from './pages/FahrzeugQRScanPage';
import PoolFahrzeugeSeite from './pages/PoolFahrzeugeSeite';
import WerkstattKiosk from './pages/WerkstattKiosk';
import WerkstattKundenPage from './pages/WerkstattKundenPage';
import GoogleCalendarCallback from './pages/GoogleCalendarCallback';
// GmailCallback ist jetzt in GoogleCalendarCallback integriert (shared callback)
import CRMPage from './pages/CRMPage';
import DublettenTop20Page from './pages/DublettenTop20Page';
import CrmDublettenPage from './pages/CrmDublettenPage';
import InformationsschreibenPage from './pages/InformationsschreibenPage';
import WerkstattPortal, { WerkstattLogin } from './pages/WerkstattPortal';
import AnwaltPortal, { AnwaltLogin } from './pages/AnwaltPortal';
import KundenPortal, { KundenLogin } from './pages/KundenPortal';
import KundenLinkPortal from './pages/KundenLinkPortal';
import MarketingPage from './pages/MarketingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VorgaengePage from './pages/VorgaengePage';
import WerkstattKundeDetailPage from './pages/WerkstattKundeDetailPage';
import WerkstattAuftragDetailPage from './pages/WerkstattAuftragDetailPage';
import WerkstattAngebotePage from './pages/WerkstattAngebotePage';
import WerkstattAuftraegePage from './pages/WerkstattAuftraegePage';
import LiquiditaetPage from './pages/LiquiditaetPage';
import TransaktionKlaerungPage from './pages/TransaktionKlaerungPage';
import MietvertragPage from './pages/MietvertragPage';
import MietwagenKiosk from './pages/MietwagenKiosk';
import PosteingangPage from './pages/PosteingangPage';
import EingangskorbPage from './pages/EingangskorbPage';
import BuchhaltungVorbereitungPage from './pages/BuchhaltungVorbereitungPage';
import BuchhaltungEinOrtPage from './pages/BuchhaltungEinOrtPage';
import FinanzenCockpitPage from './pages/FinanzenCockpitPage';
import UeberweisungenPage from './pages/UeberweisungenPage';
import BenachrichtigungenPage from './pages/BenachrichtigungenPage';
import AgentenBriefingEditor from './components/AgentenBriefingEditor';
import Sign from './pages/Sign';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

/** Route Guard — prüft ob der Benutzer Zugriff auf Admin-only Seiten hat */
function AdminGuard({ children }: { children: ReactNode }) {
  const { user, loading, profile } = useAuth();
  const role = profile?.rolle;

  // Wenn Auth noch lädt oder Profil noch nicht geladen → Loading statt Redirect
  if (loading || (user && !profile)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Admin hat immer Zugriff
  if (role === 'admin') return <>{children}</>;

  // Kein Zugriff
  return <Redirect to='/kommunikation' />;
}

/** Modul-Guard — prüft ob der Benutzer Zugriff auf ein bestimmtes Modul hat */
function ModuleGuard({ moduleId, children }: { moduleId: string; children: ReactNode }) {
  const { user, loading, profile } = useAuth();
  const role = profile?.rolle;
  const modulePerms = profile?.modulePermissions as Record<string, boolean> | null | undefined;

  // Wenn Auth noch lädt oder Profil noch nicht geladen → Loading statt Redirect
  if (loading || (user && !profile)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Admin hat immer Zugriff
  if (role === 'admin') return <>{children}</>;

  // Kernmodule sind für ALLE eingeloggten User zugänglich
  const CORE_MODULES = ['kommunikation', 'aufgaben', 'kalender', 'einstellungen'];
  if (CORE_MODULES.includes(moduleId)) return <>{children}</>;

  // Wenn module_permissions existieren und das Modul freigeschaltet ist
  if (modulePerms && modulePerms[moduleId] === true) return <>{children}</>;

  // Fallback für Sachbearbeiter ohne module_permissions: Rollen-basiert
  if (!modulePerms && (role === 'sachbearbeiter' || role === 'mitarbeiter')) {
    // Sachbearbeiter dürfen Akten und Dokumente sehen
    if (role === 'sachbearbeiter' && ['akten', 'dokumente'].includes(moduleId)) return <>{children}</>;
  }

  // Kein Zugriff — aber nur redirecten wenn Profil definitiv geladen ist
  return <Redirect to='/kommunikation' />;
}

/** Loading-Spinner während Auth-Check */
function AuthLoading() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B91C1C] mx-auto mb-3" />
        <p className="text-sm text-gray-500">Wird geladen...</p>
      </div>
    </div>
  );
}

/** Protected Route Wrapper — leitet zu /login wenn nicht eingeloggt */
function ProtectedRoutes() {
  const { user, loading, profile } = useAuth();
  const [location] = useLocation();
  const isAdmin = profile?.rolle === 'admin';

  if (loading) return <AuthLoading />;

  // Nicht eingeloggt → Login-Seite (außer man ist schon dort)
  if (!user && location !== '/login') {
    return <Redirect to="/login" />;
  }

  return (
    <Switch>
      {/* Login Route — wenn schon eingeloggt → Dashboard (Admin) oder Kommunikation (Mitarbeiter) */}
      <Route path="/login">
        {user ? <Redirect to={isAdmin ? '/' : '/kommunikation'} /> : <LoginPage />}
      </Route>

      {/* Werkstatt-Portal (separates Layout, kein SV-Büro-Navigation) */}
      <Route path='/werkstatt/login' component={WerkstattLogin} />
      <Route path='/werkstatt/dashboard' component={WerkstattPortal} />
      <Route path='/werkstatt'>
        {user ? <Redirect to='/werkstatt/dashboard' /> : <Redirect to='/werkstatt/login' />}
      </Route>

      {/* Anwalt-Portal (separates Layout, Dunkelblau-Design) */}
      <Route path='/anwalt/login' component={AnwaltLogin} />
      <Route path='/anwalt/dashboard' component={AnwaltPortal} />
      <Route path='/anwalt'>
        {user ? <Redirect to='/anwalt/dashboard' /> : <Redirect to='/anwalt/login' />}
      </Route>

      {/* Kiosk Routes sind jetzt außerhalb von ProtectedRoutes (App-Level) */}

      {/* Passwort-Reset-Seite (kein Layout, kein Auto-Login) */}
      <Route path='/reset-password' component={ResetPasswordPage} />

      {/* Google Calendar OAuth Callback (fullscreen, no layout) */}
      <Route path='/auth/google/callback' component={GoogleCalendarCallback} />
      {/* Gmail nutzt jetzt auch /auth/google/callback — Redirect fuer Kompatibilitaet */}
      <Route path='/auth/gmail/callback'>
        {() => { window.location.href = '/auth/google/callback' + window.location.search; return null; }}
      </Route>

      {/* All other routes use DashboardLayout */}
      <Route>
        <DashboardLayout>
          <Switch>
            {/* Dashboard — Admin-only, Mitarbeiter werden zu /kommunikation umgeleitet */}
            <Route path='/'>
              <ModuleGuard moduleId='dashboard'><Dashboard /></ModuleGuard>
            </Route>
            <Route path='/dashboard'>
              <ModuleGuard moduleId='dashboard'><Dashboard /></ModuleGuard>
            </Route>

            {/* CRM — Admin-only */}
            <Route path='/crm'>
              <ModuleGuard moduleId='crm'><Redirect to='/crm/kontakte' /></ModuleGuard>
            </Route>
            <Route path='/crm/dubletten-top20'>
              <ModuleGuard moduleId='crm'><DublettenTop20Page /></ModuleGuard>
            </Route>
            <Route path='/crm/dubletten'>
              <AdminGuard><CrmDublettenPage /></AdminGuard>
            </Route>
            <Route path='/crm/:tab'>
              {(params: any) => <ModuleGuard moduleId='crm'><CRMPage {...params} /></ModuleGuard>}
            </Route>

            {/* Akten — Admin-only (oder per module_permissions) */}
            <Route path='/akten'>
              <ModuleGuard moduleId='akten'><Redirect to='/akten/gutachten' /></ModuleGuard>
            </Route>
            <Route path='/akten/:betrieb'>
              {(params: any) => <ModuleGuard moduleId='akten'><AktenPage {...params} /></ModuleGuard>}
            </Route>
            <Route path='/akten/:betrieb/:aktenNr/:section?'>
              {(params: any) => <ModuleGuard moduleId='akten'><AkteDetail {...params} /></ModuleGuard>}
            </Route>

            {/* Informationsschreiben — Admin-only */}
            <Route path='/informationsschreiben'>
              <ModuleGuard moduleId='informationsschreiben'><InformationsschreibenPage /></ModuleGuard>
            </Route>

            {/* Marketing — Admin-only */}
            <Route path='/marketing'>
              <ModuleGuard moduleId='marketing'><MarketingPage /></ModuleGuard>
            </Route>
            <Route path='/marketing/:tab'>
              {(params: any) => <ModuleGuard moduleId='marketing'><MarketingPage {...params} /></ModuleGuard>}
            </Route>

            {/* Agenten-Briefing — Admin-only (Inhaber verwaltet Agenten-Briefings/Rechte) */}
            <Route path='/agenten-briefing'>
              <AdminGuard><AgentenBriefingEditor /></AdminGuard>
            </Route>

            {/* Zentrale Module — für alle Rollen sichtbar */}
            <Route path="/aufgaben" component={AufgabenPage} />
            <Route path="/kalender" component={KalenderPage} />
            <Route path="/stellungnahmen" component={StellungnahmenPage} />
            <Route path="/kommunikation/entwuerfe" component={EntwuerfeFreigabePage} />
            <Route path="/kommunikation" component={KommunikationPage} />
            <Route path="/benachrichtigungen" component={BenachrichtigungenPage} />

            {/* Kontakte — Admin-only */}
            <Route path="/kontakte/:id/:section">
              {(params: any) => <ModuleGuard moduleId='kontakte'><KontaktDetailPage {...params} /></ModuleGuard>}
            </Route>
            <Route path="/kontakte/:id">
              {(params: any) => <ModuleGuard moduleId='kontakte'><KontaktDetailPage {...params} /></ModuleGuard>}
            </Route>
            <Route path="/kontakte">
              <ModuleGuard moduleId='kontakte'><KontaktePage /></ModuleGuard>
            </Route>

            {/* ─── FINANZEN-COCKPIT (Feature finanzen-cockpit) ─── */}
            {/* Neuer EIN-Menüpunkt: das Mandanten-Cockpit. Default-Firma sv_buero. */}
            <Route path="/finanzen">
              <ModuleGuard moduleId='finanzen'><FinanzenCockpitPage /></ModuleGuard>
            </Route>
            <Route path="/finanzen/:firma/:section?">
              {(params: any) => <ModuleGuard moduleId='finanzen'><FinanzenCockpitPage {...params} /></ModuleGuard>}
            </Route>

            {/* Finanzen — bestehende Direkt-Routen bleiben UNVERÄNDERT (Deep-Links).
                Sie sind nur aus dem Sidebar-Menü entfernt; das Cockpit verwendet
                dieselben Seiten/Komponenten wieder. */}
            <Route path="/buchhaltung">
              <ModuleGuard moduleId='finanzen'><FinanzenDashboard /></ModuleGuard>
            </Route>
            {/* Konsolidierte Buchhaltung — EIN Ort (Feature buchhaltung-ein-ort) */}
            <Route path="/buchhaltung/zentrale">
              <ModuleGuard moduleId='finanzen'><BuchhaltungEinOrtPage /></ModuleGuard>
            </Route>
            <Route path="/buchhaltung/rechnungen">
              <ModuleGuard moduleId='finanzen'><BuchhaltungPage /></ModuleGuard>
            </Route>
            <Route path="/buchhaltung/datev">
              <ModuleGuard moduleId='finanzen'><BuchhaltungPage /></ModuleGuard>
            </Route>
            <Route path="/buchhaltung/konten">
              <ModuleGuard moduleId='finanzen'><BuchhaltungPage /></ModuleGuard>
            </Route>
            {/* Legacy-Route „Eingangskorb“ — bleibt erhalten (Deep-Links), Menü zeigt jetzt auf /buchhaltung/zentrale */}
            <Route path="/buchhaltung/eingangskorb">
              <ModuleGuard moduleId='finanzen'><EingangskorbPage /></ModuleGuard>
            </Route>
            {/* Legacy-Route „Buchhaltungsvorbereitung“ — bleibt erhalten (Deep-Links) */}
            <Route path="/buchhaltung/vorbereitung">
              <ModuleGuard moduleId='finanzen'><BuchhaltungVorbereitungPage /></ModuleGuard>
            </Route>
            <Route path="/buchhaltung/ueberweisungen">
              <ModuleGuard moduleId='finanzen'><UeberweisungenPage /></ModuleGuard>
            </Route>
            <Route path="/liquiditaet">
              <ModuleGuard moduleId='finanzen'><LiquiditaetPage /></ModuleGuard>
            </Route>
            <Route path="/transaktionen/klaerung">
              <ModuleGuard moduleId='finanzen'><TransaktionKlaerungPage /></ModuleGuard>
            </Route>

            {/* Dokumente — Admin-only */}
            <Route path="/dokumente">
              <ModuleGuard moduleId='dokumente'><DokumentePage /></ModuleGuard>
            </Route>

            {/* Posteingang — Dokumenten-Scanner mit KI-Analyse */}
            <Route path="/posteingang">
              <ModuleGuard moduleId='dokumente'><PosteingangPage /></ModuleGuard>
            </Route>

            {/* Vorgänge — Admin-only */}
            <Route path="/vorgaenge">
              <ModuleGuard moduleId='vorgaenge'><VorgaengePage /></ModuleGuard>
            </Route>

            {/* Recht & Forderungen — Admin-only */}
            <Route path="/recht">
              <ModuleGuard moduleId='akten'><RechtsAktenPage /></ModuleGuard>
            </Route>
            {/* Forderungs-Cockpit — morgendliche Fall-Prüfungs-Ansicht (Feature forderungs-cockpit).
                Kein ModuleGuard: das Modul ist nicht in der Modul-Registry/den module_permissions
                registriert. Sichtbarkeit ist über den Menüeintrag (requiredRoles:['admin'] in
                navigation.ts) geregelt; Auth + DashboardLayout kommen weiterhin vom Wrapper. */}
            <Route path="/forderungs-cockpit">
              <ForderungsCockpitPage />
            </Route>
            {/* E-Mail-Postfächer verbinden — geteilte Gmail-Postfächer (neu-)verbinden.
                Kein ModuleGuard (analog Forderungs-Cockpit): Sichtbarkeit über den
                Menüeintrag (requiredRoles:['admin'] in navigation.ts). Auth + DashboardLayout
                kommen vom Wrapper. */}
            <Route path="/mail-verbinden">
              <MailboxReconnectPage />
            </Route>
            {/* Forderungsmanager — Drawer-basiert (issue #75), erreichbar von überall */}
            <Route path="/forderungen">
              <ModuleGuard moduleId='akten'><ForderungsmanagerPage /></ModuleGuard>
            </Route>
            {/* Legacy Mietwagen-Haftpflicht Forderungsmanagement — für bestehende Deep-Links */}
            <Route path="/forderungen/mietwagen-haftpflicht">
              <ModuleGuard moduleId='akten'><ForderungsmanagementPage /></ModuleGuard>
            </Route>

            {/* Pool-Fahrzeuge — Reservierung + Kalender-UI (Issue #164) */}
            <Route path="/pool-fahrzeuge">
              <PoolFahrzeugeSeite />
            </Route>

            {/* Fahrzeug-QR-Scan — offen für alle Mitarbeiter (Poolfahrzeug-Übernahme) */}
            <Route path="/fahrzeuge/qr-scan">
              <FahrzeugQRScanPage />
            </Route>

            {/* Fahrzeug-Detail — Admin-only */}
            <Route path="/fahrzeuge/:id/:section?">
              {(p: any) => <ModuleGuard moduleId='akten'><FahrzeugDetail {...p} /></ModuleGuard>}
            </Route>

            {/* Fahrzeuge — Admin-only */}
            <Route path="/fahrzeuge">
              <ModuleGuard moduleId='akten'><FahrzeugePage /></ModuleGuard>
            </Route>

            {/* Werkstatt-Kundenverwaltung (intern, SV-Büro) — Admin/Werkstatt-Modul */}
            <Route path="/werkstatt/kunden/:id">
              {(p: any) => (
                <ModuleGuard moduleId='werkstatt'>
                  <WerkstattKundeDetailPage {...p} />
                </ModuleGuard>
              )}
            </Route>

            {/* Werkstatt-Aufträge Kanban-Board (Issue #392) */}
            <Route path="/werkstatt/auftraege/:id">
              {(p: any) => (
                <ModuleGuard moduleId='werkstatt'>
                  <WerkstattAuftragDetailPage {...p} />
                </ModuleGuard>
              )}
            </Route>
            <Route path="/werkstatt/auftraege">
              <ModuleGuard moduleId='werkstatt'><WerkstattAuftraegePage /></ModuleGuard>
            </Route>

            {/* Werkstatt-Auftrag-Detail legacy route (Foto-Upload — Issue #394) */}
            <Route path="/werkstatt/auftrag/:id">
              {(p: any) => (
                <ModuleGuard moduleId='werkstatt'>
                  <WerkstattAuftragDetailPage {...p} />
                </ModuleGuard>
              )}
            </Route>

            {/* Mietvertrag — Admin-only */}
            <Route path="/mietvertrag">
              <ModuleGuard moduleId='akten'><MietvertragPage /></ModuleGuard>
            </Route>

            {/* Werkstatt-Angebote (Kostenvoranschläge) — Issue #396 */}
            <Route path="/werkstatt/angebote">
              <ModuleGuard moduleId='werkstatt'><WerkstattAngebotePage /></ModuleGuard>
            </Route>

            {/* Werkstatt-Kundenverwaltung — Admin-only (Issue #401) */}
            <Route path="/werkstatt/kunden">
              <ModuleGuard moduleId='werkstatt'><WerkstattKundenPage /></ModuleGuard>
            </Route>

            {/* Einstellungen — für alle (eigene Einstellungen) */}
            <Route path="/einstellungen/:section?" component={EinstellungenPage} />

            {/* Fallback — Admin → Dashboard, Mitarbeiter → Kommunikation */}
            <Route>
              <Redirect to={isAdmin ? '/' : '/kommunikation'} />
            </Route>
          </Switch>
        </DashboardLayout>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster position="bottom-right" />
          <AuthProvider>
            <AppProvider>
              <Switch>
                {/* Unterschriften-Signing-Page OHNE Auth — Zugang via Mail-Token (Issue #649) */}
                <Route path='/sign/:token' component={Sign} />

                {/* Kunden-Link-Portal OHNE Auth — Zugang via generiertem Token + Passwort (Issue #71) */}
                <Route path='/portal/:token' component={KundenLinkPortal} />

                {/* Kunden-Portal OHNE Auth — Zugang via Auftragsnummer + Nachname */}
                <Route path='/kunde/login' component={KundenLogin} />
                <Route path='/kunde/dashboard' component={KundenPortal} />
                <Route path='/kunde'>
                  {() => {
                    const hasAccess = sessionStorage.getItem('kunde_zugang');
                    if (hasAccess) {
                      window.location.href = '/kunde/dashboard';
                    } else {
                      window.location.href = '/kunde/login';
                    }
                    return null;
                  }}
                </Route>
                {/* Kiosk-Routen OHNE Auth — öffentlich zugänglich via PIN */}
                <Route path='/kiosk/kunden'>
                  {() => {
                    // Prüfen ob Kiosk-Zugang via PIN gewährt wurde
                    const hasAccess = sessionStorage.getItem('kiosk_access') === 'true';
                    if (!hasAccess) {
                      window.location.href = '/login';
                      return null;
                    }
                    return <KundenKiosk />;
                  }}
                </Route>
                <Route path='/kiosk/mietwagen'>
                  {() => {
                    const hasAccess = sessionStorage.getItem('kiosk_access') === 'true';
                    if (!hasAccess) {
                      window.location.href = '/login';
                      return null;
                    }
                    return <MietwagenKiosk />;
                  }}
                </Route>
                <Route path='/kiosk/werkstatt'>
                  {() => {
                    const hasAccess = sessionStorage.getItem('kiosk_access') === 'true';
                    if (!hasAccess) {
                      window.location.href = '/login';
                      return null;
                    }
                    return <WerkstattKiosk />;
                  }}
                </Route>
                {/* Alle anderen Routen — Auth erforderlich */}
                <Route>
                  <ProtectedRoutes />
                </Route>
              </Switch>
            </AppProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
